import { useState } from "react";
import { Sparkle, MagicWand, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Reusable "tell the AI what you want" panel. Appears on every builder.
 * Props:
 *  - onFill(description): fills the builder's fields (required)
 *  - onGenerate(description): fills + generates the final artifact (optional)
 *  - placeholder, testid, hint
 */
export const AiAssistPanel = ({ onFill, onGenerate, placeholder, testid = "ai-assist", hint }) => {
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(null); // "fill" | "generate" | null

  const run = async (mode) => {
    if (!desc.trim()) {
      toast.error("Describe what you want first");
      return;
    }
    setBusy(mode);
    try {
      if (mode === "generate" && onGenerate) await onGenerate(desc);
      else await onFill(desc);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid={`${testid}-panel`}
      className="relative overflow-hidden border border-[#B37FEB]/30 bg-black/40 backdrop-blur-md p-4"
    >
      {/* AI beam along the top edge */}
      <span className="ai-beam absolute top-0 left-0 h-px w-full" aria-hidden />
      <div className="flex items-center gap-2 mb-3">
        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#B37FEB]/15 border border-[#B37FEB]/40">
          <Sparkle size={13} weight="fill" className="text-[#C79BF0]" />
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#C79BF0]">Ask AI</span>
        <span className="ml-auto font-mono text-[9px] text-[#71717A] hidden sm:block">natural language → config</span>
      </div>

      <textarea
        data-testid={`${testid}-input`}
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        rows={2}
        placeholder={placeholder || "Describe what you want this tool to do…"}
        className="w-full bg-transparent border-b border-zinc-800 focus:border-[#B37FEB] text-white placeholder-zinc-600 font-mono text-sm outline-none py-2 resize-y transition-colors duration-200"
      />

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button
          data-testid={`${testid}-fill-btn`}
          onClick={() => run("fill")}
          disabled={!!busy}
          className="flex items-center gap-1.5 bg-[#B37FEB] text-[#0A0A0B] font-medium px-4 py-2 text-xs hover:bg-[#a066e0] transition-colors duration-200 disabled:opacity-50"
        >
          <MagicWand size={14} weight="fill" /> {busy === "fill" ? "Configuring…" : "Fill the fields"}
        </button>
        {onGenerate && (
          <button
            data-testid={`${testid}-generate-btn`}
            onClick={() => run("generate")}
            disabled={!!busy}
            className="flex items-center gap-1.5 border border-[#B37FEB]/50 text-[#C79BF0] px-4 py-2 text-xs hover:bg-[#B37FEB]/10 transition-colors duration-200 disabled:opacity-50"
          >
            <Lightning size={14} weight="fill" /> {busy === "generate" ? "Generating…" : "Fill & generate"}
          </button>
        )}
        <span className="font-mono text-[10px] text-[#71717A]">{hint || "Review the result before you ship it."}</span>
      </div>
    </div>
  );
};
